import React from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { AdminLoginForm } from "../components/AdminLoginForm";
import AdminDashboard from "../../../admin/presentation/AdminDashboard";
import { LOGO_PATH, LOGO_ALT } from "../../../../constants/logo";

export const AdminLoginPage: React.FC = () => {
  const {
    adminLogin,
    logout,
    isAuthenticated,
    loading, // Keep for initial auth check
    loginLoading, // Use for login action
    isAdmin,
    isAdminOrCfo,
    error,
    loginError, // Use for login errors
    clearError,
    clearLoginError,
  } = useAuth();
  const navigate = useNavigate();

  const handleAdminLogin = async (email: string, password: string) => {
    console.log("Admin login attempt");
    clearLoginError();

    try {
      const result = await adminLogin({ email, password });
      if (result.success) {
        navigate("/admin/dashboard");
      }
    } catch (error) {
      console.error("Admin login error:", error);
    }
  };

  const handleAdminLogout = () => {
    logout();
    navigate("/");
  };

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If user is authenticated and is admin or CFO, show dashboard
  if (isAuthenticated && isAdminOrCfo()) {
    return <AdminDashboard onLogout={handleAdminLogout} />;
  }

  // If user is authenticated but not admin/CFO, redirect to unauthorized
  if (isAuthenticated && !isAdminOrCfo()) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Show admin login form
  return (
    <div className="flex h-screen w-full flex-col">
      <div className="flex h-full w-full flex-wrap items-start bg-default-background mobile:flex-col mobile:flex-wrap mobile:gap-0">
        {/* Left Side - Branding */}
        <div className="flex max-w-[576px] grow shrink-0 basis-0 flex-col items-center gap-12 self-stretch bg-neutral-800 px-12 py-12 mobile:h-auto mobile:w-full mobile:flex-none">
          <div className="flex w-full max-w-[448px] grow shrink-0 basis-0 flex-col items-start justify-center gap-12 mobile:h-auto mobile:w-full mobile:max-w-[448px] mobile:flex-none">
            <img
              alt={LOGO_ALT}
              className="h-8 flex-none object-cover invert"
              src={LOGO_PATH}
            />
            <div className="flex flex-col items-start justify-center gap-16 pb-32 mobile:px-0 mobile:py-0">
              <div className="flex flex-col items-start gap-2">
                <span className="text-heading-2 font-heading-2 text-white">
                  Admin Access
                </span>
                <span className="text-heading-3 font-heading-3 text-brand-200">
                  Sign in as admin to access the administrative dashboard.
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Admin Login Form */}
        <div className="flex grow shrink-0 basis-0 flex-col items-center justify-center gap-6 self-stretch border-l border-solid border-neutral-border px-12 py-12">
          <AdminLoginForm
            onSubmit={handleAdminLogin}
            isLoading={loginLoading}
            error={loginError}
          />
        </div>
      </div>
    </div>
  );
};
